import { ThemeProvider } from "@mui/material";
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router';
import AHDate from "_shared/dates/date";

const Copyright = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  ...theme.applyStyles('light', {
    color: 'rgba(0, 0, 0, 0.54)'
  })
}));

const FooterLinks = styled('section')(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  '& ul': {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    listStyleType: 'none',
    margin: 0,
    padding: 0,
    rowGap: theme.spacing(0.5),
    '& li': {
      '&:last-child': {
        border: 'none'
      },
      borderRight: '1px solid',
      borderColor: theme.palette.mode === 'light' ? '#3f51b5' : '#fff',
      flexBasis: 'auto',
      padding: '0 1em',
      textAlign: 'center'
    },
    // Once the row wraps, the divider on the last item of each visual line points at nothing —
    // there is no selector for "last on this line" — so the narrow layout spaces the links instead
    // of ruling between them.
    [theme.breakpoints.down('md')]: {
      '& li': {
        border: 'none',
        padding: theme.spacing(0, 1)
      }
    }
  }
}));

const FooterContainer = styled('footer')(({ theme }) => ({
  display: 'block',
  flexShrink: 0,
  fontSize: '0.8em',
  margin: 'auto',
  padding: theme.spacing(2, 0),
  textAlign: "center",
  width: '100%',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2.75, 0, 2.75, 0)
  }
}));

const FooterLink = styled(Link)(({ theme }) => ({
  color: theme.palette.mode === 'light' ? '#3f51b5' : 'white'
}));

type Props = {
  mode?: string;
};

/**
 * The links are rendered at every width. They used to be dropped below `md`, and nothing else in
 * the app links to `/privacy-policy` or `/terms-of-use`, so on a phone they were unreachable
 * altogether rather than merely inconvenient. The list wraps instead.
 */
export default function Footer(props: Readonly<Props>) {
  return (
    <ThemeProvider theme={theme => ({
      ...theme,
      palette: {
        mode: props.mode ?? 'light'
      }
    })}>
      <FooterContainer id='siteFooter'>
        <Copyright variant="body2" color="textSecondary">
          {`Copyright \u00A9 Attorney Hub, LLC ${new AHDate().year}`}
        </Copyright>
        <FooterLinks>
          <ul>
            <li><FooterLink to="/privacy-policy">Privacy Policy</FooterLink></li>
            <li><FooterLink to="/terms-of-use">Terms of Use</FooterLink></li>
            <li><FooterLink to="/about-us">About Us</FooterLink></li>
            <li><FooterLink to="mailto:contactus@attorneyhub.com">Contact Us</FooterLink></li>
          </ul>
        </FooterLinks>
      </FooterContainer>
    </ThemeProvider>
  );
}
